import { Component, OnInit, ChangeDetectorRef, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Subscription } from 'rxjs';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';

import { IntegrationsService } from '../../integrations.service';
import {
  OneDriveDatabase,
  OneDriveDataSource,
  OneDriveFlatNode,
} from './one-drive-datasource/one-drive-datasource.service';

@Component({
  selector: 'one-drive-integration',
  templateUrl: './one-drive-integration.component.html',
  styleUrls: ['./one-drive-integration.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OneDriveIntegrationComponent extends OnDestroyMixin implements OnInit {
  public isSynced: boolean;
  public isSharePointUndefined: boolean;
  public treeControl: FlatTreeControl<OneDriveFlatNode>;
  public dataSource: OneDriveDataSource;
  public dataSourceIsLoading: boolean = true;

  constructor(
    private _router: Router,
    private _integrationsService: IntegrationsService,
    private _database: OneDriveDatabase,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.treeControl = new FlatTreeControl<OneDriveFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new OneDriveDataSource(this.treeControl, _database);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  async ngOnInit(): Promise<void> {
    this.dataSource.data = await this._database.initialData();
    this.isSharePointUndefined = this._database.sharePointResponse;
    this.dataSourceIsLoading = false;
    this._changeDetectorRef.markForCheck();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Component functions
  // -----------------------------------------------------------------------------------------------------
  public getLevel = (node: OneDriveFlatNode) => node.level;

  public isExpandable = (node: OneDriveFlatNode) => node.expandable;

  public hasChild = (_: number, _nodeData: OneDriveFlatNode) => _nodeData.expandable;

  public markCheckNode(checked: boolean, node: OneDriveFlatNode): void {
    if (checked) {
      this.dataSource.data = this.dataSource.data.map((item) => {
        if (
          item.item.item_id === node.item.item_id &&
          item.item.drive_id === node.item.drive_id &&
          item.item.site_id === node.item.site_id &&
          item.level === node.level
        ) {
          item.checked = checked;
        } else {
          item.checkDisabled = true;
        }

        return item;
      });
    } else {
      this.dataSource.data = this.dataSource.data.map((item) => {
        item.checked = false;
        item.checkDisabled = false;
        return item;
      });
    }
    this._changeDetectorRef.markForCheck();
  }

  public syncOneDrivePanel(): Subscription {
    const oneDriveObj = {
      one_drive: true,
    };

    return this._integrationsService
      .syncOneDriveSingleOption(oneDriveObj)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this._successfullySynced();
        this._changeDetectorRef.markForCheck();
      });
  }

  public syncChildNode(nodeValues: OneDriveFlatNode): Subscription {
    const childValues = {
      drive_id: nodeValues.item.drive_id,
      item_id: nodeValues.item.item_id,
      site_id: nodeValues.item.site_id,
    };
    return this._integrationsService
      .syncOneDriveSingleOption(childValues)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this._successfullySynced();
        this._changeDetectorRef.markForCheck();
      });
  }

  /**
   * FUnction triggered after sync is successfull and responsible for showing a success friendly message and
   * after 4 seconds, navigate to 'integrations' page.
   */
  private _successfullySynced(): void {
    this.isSynced = true;
    setTimeout(() => {
      this._router.navigate(['/settings/integrations']);
    }, 4000);
  }
}
